import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const CATS = [
  {
    title: "Pipe, Valves & Fittings",
    tag: "PVF",
    items: ["Carbon & stainless pipe", "Gate / globe / ball valves", "Weld & threaded fittings", "Flanges"],
    img: "https://images.unsplash.com/photo-1538474705339-e87de81450e8",
    span: "md:col-span-2 md:row-span-2",
    big: true,
  },
  {
    title: "Gaskets & Sealing",
    tag: "SEAL",
    items: ["Spiral wound", "Ring joint", "Sheet gaskets"],
    span: "md:col-span-1",
  },
  {
    title: "Stud Bolts & Fasteners",
    tag: "FAST",
    items: ["Stud bolts & nuts", "Machine bolts", "Anchors & screws"],
    span: "md:col-span-1",
  },
  {
    title: "Blinds & Spec Items",
    tag: "SPEC",
    items: ["Spectacle blinds", "Spacers & paddles", "Orifice plates"],
    span: "md:col-span-1",
  },
  {
    title: "Lumber & Jobsite Materials",
    tag: "BUILD",
    items: ["Plywood & sheathing", "Wood studs & dunnage", "Nails & hardware"],
    img: "https://images.pexels.com/photos/33996167/pexels-photo-33996167.jpeg",
    span: "md:col-span-1",
    imageCard: true,
  },
];

const reveal = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 } }),
};

export const Capabilities = ({ onQuote }) => {
  return (
    <section id="capabilities" className="relative py-24 md:py-36 bg-card border-y border-border" data-testid="capabilities">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div className="max-w-2xl">
            <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-primary flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-accent" /> Capabilities
            </span>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl md:text-6xl mt-6 leading-[0.95] text-foreground">
              A full catalog, one supplier
            </h2>
          </div>
          <p className="max-w-sm text-steel text-base leading-relaxed">
            We don't publish a fixed catalog — because we source virtually anything a
            project requires. A snapshot of what we handle every day:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-border auto-rows-[minmax(200px,auto)]">
          {CATS.map((c, i) => (
            <motion.div
              key={c.title}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className={`group relative bg-background p-7 flex flex-col justify-between overflow-hidden ${c.span}`}
              data-testid={`capability-${c.tag}`}
            >
              {(c.img && (c.big || c.imageCard)) && (
                <div className="absolute inset-0 z-0">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover img-grayscale opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
                </div>
              )}
              <div className="relative z-10 flex items-start justify-between">
                <span className="font-mono-tech text-[10px] tracking-[0.25em] text-accent border border-accent/40 px-2 py-1">
                  {c.tag}
                </span>
                <ArrowUpRight size={18} className="text-steel group-hover:text-primary group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
              </div>
              <div className="relative z-10 mt-8">
                <h3 className={`font-display font-bold tracking-tight text-foreground ${c.big ? "text-3xl md:text-4xl" : "text-2xl"}`}>
                  {c.title}
                </h3>
                <ul className="mt-4 space-y-1.5">
                  {c.items.map((it) => (
                    <li key={it} className="text-sm text-steel flex items-center gap-2">
                      <span className="w-1 h-1 bg-primary shrink-0" /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}

          {/* CTA cell */}
          <motion.button
            custom={CATS.length}
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            onClick={onQuote}
            data-testid="capability-cta"
            className="group bg-primary text-primary-foreground p-7 flex flex-col justify-between text-left md:col-span-1 hover:bg-accent transition-colors duration-300"
          >
            <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase opacity-80">Need something else?</span>
            <div>
              <h3 className="font-display font-extrabold text-3xl uppercase tracking-tight leading-none">Ask us<br />anything</h3>
              <span className="mt-4 inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.2em]">
                Get a quote <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </span>
            </div>
          </motion.button>
        </div>
      </div>
    </section>
  );
};
